// src/lib/callFunction.js
import supabase, { getSupabaseAnonKey, getSupabaseBaseUrl } from "@/services/supabaseClient";
import { resolveValidatedSession } from "@/lib/authSession";

const DEFAULT_TIMEOUT_MS = 15000;
const SESSION_RECOVERY_TIMEOUT_MS = 8000;
const SESSION_RECOVERY_INTERVAL_MS = 200;
const SESSION_EXPIRY_SKEW_MS = 60_000;
const AUTH_TIMEOUT_MS = 12000;

function lowerText(value) {
  return String(value || "").trim().toLowerCase();
}

function isJwtAuthError(error) {
  const status = Number(error?.status || 0);
  const bodyCode = Number(error?.body?.code || 0);
  const message = lowerText(error?.message || error?.raw || error?.body?.message || error?.body?.error);

  return (
    status === 401 ||
    bodyCode === 401 ||
    message.includes("invalid jwt") ||
    message.includes("jwt expired") ||
    message.includes("session expired")
  );
}

function withAuthTimeout(promise, label = "Auth request") {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      const err = new Error(`${label} timed out`);
      err.code = "auth_timeout";
      setTimeout(() => reject(err), AUTH_TIMEOUT_MS);
    }),
  ]);
}

function withTimeout(promiseFactory, ms, label = "Request") {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);

  return {
    run: (async () => {
      try {
        return await promiseFactory(ctrl.signal);
      } catch (e) {
        if (e?.name === "AbortError") {
          const err = new Error(`${label} timed out`);
          err.code = "timeout";
          throw err;
        }
        throw e;
      } finally {
        clearTimeout(t);
      }
    })(),
  };
}

async function readSessionOnce() {
  try {
    const { data } = await withAuthTimeout(supabase.auth.getSession(), "Read session");
    return data?.session || null;
  } catch {
    return null;
  }
}

async function waitForSessionRecovery() {
  const startedAt = Date.now();
  while (Date.now() - startedAt < SESSION_RECOVERY_TIMEOUT_MS) {
    const session = await readSessionOnce();
    if (session?.access_token) return session;
    await new Promise((resolve) => setTimeout(resolve, SESSION_RECOVERY_INTERVAL_MS));
  }
  return null;
}

async function getAccessToken({ refreshIfMissing = false, forceRefresh = false } = {}) {
  const base = getSupabaseBaseUrl();
  const anon = getSupabaseAnonKey();

  async function refreshViaAuthApi(refreshToken) {
    if (!refreshToken || !base || !anon) return null;

    try {
      const res = await withAuthTimeout(
        fetch(`${base}/auth/v1/token?grant_type=refresh_token`, {
          method: "POST",
          headers: {
            apikey: anon,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        }),
        "Refresh session",
      );

      const payload = await res.json().catch(() => null);
      if (!res.ok || !payload?.access_token) return null;

      const nextSession = {
        ...payload,
        refresh_token: payload.refresh_token || refreshToken,
      };

      try {
        await supabase.auth.setSession({
          access_token: nextSession.access_token,
          refresh_token: nextSession.refresh_token,
        });
      } catch {
        // Best effort only; direct token recovery is still enough for edge calls.
      }

      return nextSession;
    } catch {
      return null;
    }
  }

  async function refreshCurrentSession(currentSession = null) {
    try {
      const { data, error } = await withAuthTimeout(
        supabase.auth.refreshSession(),
        "Refresh session",
      );
      if (!error && data?.session) return data.session;
    } catch {
      // Fall through to direct refresh-token call.
    }

    return refreshViaAuthApi(currentSession?.refresh_token);
  }

  let session = await readSessionOnce();
  if (!session?.access_token && refreshIfMissing) {
    session = (await waitForSessionRecovery()) || session;
  }

  const expiresAtMs = Number(session?.expires_at || 0) * 1000;
  const canRefresh = Boolean(session?.refresh_token);
  const isExpiringSoon =
    Number.isFinite(expiresAtMs) &&
    expiresAtMs > 0 &&
    expiresAtMs <= Date.now() + SESSION_EXPIRY_SKEW_MS;

  if (forceRefresh || (canRefresh && (!session?.access_token || isExpiringSoon))) {
    const refreshed = await refreshCurrentSession(session);
    if (refreshed?.access_token) return refreshed.access_token;
    if (forceRefresh) return "";
  }

  if (session?.access_token) return session.access_token;

  if (refreshIfMissing && canRefresh) {
    const refreshed = await refreshCurrentSession(session);
    return refreshed?.access_token || "";
  }

  return "";
}

export async function resolveFunctionAccessToken(options = {}) {
  return getAccessToken(options);
}

async function fetchEdge({ url, anon, token, body, signal }) {
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: anon,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body || {}),
    signal,
  });

  const text = await res.text().catch(() => "");
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (!res.ok) {
    const err = new Error(json?.message || json?.error || text || `HTTP ${res.status}`);
    err.status = res.status;
    err.url = url;
    err.body = json;
    err.code = json?.error || "";
    err.raw = text;
    err.hasBearer = !!token;
    throw err;
  }

  return json ?? { ok: true };
}

/**
 * Calls Supabase Edge Function reliably.
 * - Always attaches apikey
 * - Attaches Authorization Bearer when available
 * - 15s watchdog
 * - If POST returns 401, refresh session once and retry once (same 15s window)
 */
export async function callFunctionRaw(name, body, opts = {}) {
  const base = getSupabaseBaseUrl();
  const anon = getSupabaseAnonKey();

  if (!base) throw new Error("Missing VITE_SUPABASE_URL");
  if (!anon) throw new Error("Missing VITE_SUPABASE_ANON_KEY");

  const url = `${base.replace(/\/$/, "")}/functions/v1/${name}`;
  const timeoutMs = Number(opts.timeoutMs || DEFAULT_TIMEOUT_MS);

  const runner = withTimeout(async (signal) => {
    // Give auth a brief chance to rehydrate from storage before the first edge call.
    let token = "";
    if (opts.skipAuth) {
      token = "";
    } else if (opts.preferFreshSession) {
      token = await getAccessToken({
        refreshIfMissing: true,
        forceRefresh: true,
      });
      if (!token) {
        const validatedSession = await resolveValidatedSession({
          signOutOnFailure: false,
        });
        token = String(validatedSession?.access_token || "").trim();
      }
    } else {
      token = String(opts.accessToken || "").trim();
      if (!token) {
        token = await getAccessToken({
          refreshIfMissing: true,
          forceRefresh: false,
        });
      }
    }

    try {
      return await fetchEdge({ url, anon, token, body, signal });
    } catch (e) {
      // If auth/JWT failed, refresh once then retry once.
      if (isJwtAuthError(e)) {
        token = await getAccessToken({ forceRefresh: true, refreshIfMissing: true });

        // still no token => real "not logged in"
        if (!token) {
          const recoveredSession = await resolveValidatedSession({
            signOutOnFailure: false,
          });
          token = String(recoveredSession?.access_token || "").trim();
        }

        if (!token) {
          const err = new Error("Not authenticated. Please log in again.");
          err.status = 401;
          err.code = "not_authenticated";
          err.url = url;
          throw err;
        }

        try {
          return await fetchEdge({ url, anon, token, body, signal });
        } catch (retryError) {
          if (isJwtAuthError(retryError)) {
            const err = new Error("Session expired. Please sign in again.");
            err.status = 401;
            err.code = "session_expired";
            err.url = url;
            throw err;
          }
          throw retryError;
        }
      }

      throw e;
    }
  }, timeoutMs, name);

  return runner.run;
}
